import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

export default function AdminSiteSettings() {
    const { settings: ctxSettings, refresh } = useSiteSettings();
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rzpInfo, setRzpInfo] = useState(null);
    const [rzpBusy, setRzpBusy] = useState(false);

    useEffect(() => {
        api.get("/site-settings")
            .then(({ data }) => setForm(data))
            .catch(() => setForm(ctxSettings));
        api.get("/admin/razorpay-mode")
            .then(({ data }) => setRzpInfo(data))
            .catch(() => setRzpInfo(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setRazorpayMode = async (mode) => {
        setRzpBusy(true);
        try {
            const { data } = await api.patch("/admin/razorpay-mode", { mode });
            setRzpInfo(data);
            await refresh();
            toast.success(`Razorpay mode set to ${mode}`);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not change mode");
        } finally {
            setRzpBusy(false);
        }
    };

    if (!form) return <div data-testid="admin-site-settings-loading" className="text-inkMuted">Loading…</div>;

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            // Build payload EXPLICITLY. Every editable string field is included with `?? ""` so
            // empty strings (e.g., a cleared tagline) are sent verbatim. NO filter, NO truthiness
            // guard, NO compactObject helper anywhere in the path.
            const payload = {
                tagline: form.tagline ?? "",
                heroImageUrl: form.heroImageUrl ?? "",
                heroHeadline: form.heroHeadline ?? "",
                heroCtaText: form.heroCtaText ?? "",
                heroCtaUrl: form.heroCtaUrl ?? "",
                whatsappNumber: form.whatsappNumber ?? "",
                chatbotGreeting: form.chatbotGreeting ?? "",
            };
            await api.patch("/admin/site-settings", payload);
            // Re-fetch the canonical settings AFTER PATCH so the local form state reflects
            // exactly what the backend now stores (don't trust an old cached value).
            const { data: fresh } = await api.get("/site-settings");
            setForm(fresh);
            await refresh(); // updates the storefront context (Header, Hero, Footer)
            toast.success("Settings saved");
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-site-settings-page">
            <span className="label-eyebrow">Storefront</span>
            <h1 className="mt-2 font-display text-4xl text-inkDark">Site settings</h1>
            <p className="mt-1 text-inkMuted">Edit hero, tagline, contact &mdash; reflected instantly across the boutique.</p>

            <form onSubmit={save} className="mt-8 max-w-3xl bg-ivory rounded-2xl border border-inkDark/5 p-6 lg:p-8 grid grid-cols-1 gap-5">
                <Field label="Tagline (shown under brand if set)">
                    <textarea
                        rows={2}
                        value={form.tagline ?? ""}
                        onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                        className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                        placeholder="Crafted for the modern Indian woman"
                        data-testid="settings-tagline-input"
                    />
                </Field>
                <Field label="Hero headline">
                    <input
                        value={form.heroHeadline ?? ""}
                        onChange={(e) => setForm({ ...form, heroHeadline: e.target.value })}
                        className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                        data-testid="settings-hero-headline-input"
                    />
                </Field>
                <Field label="Hero image URL">
                    <input
                        value={form.heroImageUrl ?? ""}
                        onChange={(e) => setForm({ ...form, heroImageUrl: e.target.value })}
                        className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                        data-testid="settings-hero-image-input"
                    />
                </Field>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <Field label="Hero CTA text">
                        <input
                            value={form.heroCtaText ?? ""}
                            onChange={(e) => setForm({ ...form, heroCtaText: e.target.value })}
                            className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                            data-testid="settings-hero-cta-text-input"
                        />
                    </Field>
                    <Field label="Hero CTA URL">
                        <input
                            value={form.heroCtaUrl ?? ""}
                            onChange={(e) => setForm({ ...form, heroCtaUrl: e.target.value })}
                            className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                            data-testid="settings-hero-cta-url-input"
                        />
                    </Field>
                </div>
                <Field label="WhatsApp number (full international, e.g. +91 98765 43210)">
                    <input
                        value={form.whatsappNumber ?? ""}
                        onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })}
                        className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                        data-testid="settings-whatsapp-input"
                    />
                </Field>
                <div className="flex justify-end pt-4 border-t border-inkDark/5">
                    <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="settings-save-button">
                        {busy ? "Saving…" : "Save settings"}
                    </button>
                </div>
            </form>

            <div className="mt-10 max-w-3xl bg-ivory rounded-2xl border border-inkDark/5 p-6 lg:p-8" data-testid="razorpay-mode-panel">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <span className="label-eyebrow">Payments</span>
                        <h2 className="mt-1 font-display text-2xl text-inkDark">Razorpay mode</h2>
                        <p className="mt-1 text-inkMuted text-sm">Switch between test and live keys at runtime &mdash; no redeploy required.</p>
                    </div>
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${rzpInfo?.mode === "live" ? "bg-red-50 text-red-700" : "bg-skyblue/40 text-inkDark"}`} data-testid="razorpay-mode-badge">
                        <span className="w-2 h-2 rounded-full bg-current opacity-70" /> {rzpInfo?.mode || "—"}
                    </span>
                </div>
                {rzpInfo ? (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div className="rounded-xl border border-inkDark/10 p-4">
                            <div className="label-eyebrow">Active key</div>
                            <div className="mt-1 font-mono text-inkDark">{rzpInfo.keyIdMasked}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setRazorpayMode("test")}
                                disabled={rzpBusy || rzpInfo.mode === "test"}
                                className={`flex-1 rounded-full px-4 py-2.5 text-sm border transition-colors ${rzpInfo.mode === "test" ? "bg-skyblue border-skyblue text-inkDark" : "bg-white border-inkDark/10 text-inkDark hover:border-skyblueDeep"} disabled:opacity-50`}
                                data-testid="razorpay-mode-test"
                            >
                                Test mode
                            </button>
                            <button
                                onClick={() => setRazorpayMode("live")}
                                disabled={rzpBusy || rzpInfo.mode === "live"}
                                className={`flex-1 rounded-full px-4 py-2.5 text-sm border transition-colors ${rzpInfo.mode === "live" ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-inkDark/10 text-inkDark hover:border-red-300 hover:text-red-700"} disabled:opacity-50`}
                                data-testid="razorpay-mode-live"
                            >
                                Live mode
                            </button>
                        </div>
                    </div>
                ) : null}
                <div className="mt-4 text-xs text-inkMuted">
                    Test card: <span className="font-mono">4111 1111 1111 1111</span>, any future expiry, any CVV. <span className="font-mono">success@razorpay</span> UPI also works.
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
